// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useMemo } from "react";
import CharacterSymbolComponent from "../character-symbol/CharacterSymbolComponent";
import styles from "./characterlist.module.css";

const sections = [
    {
        title: "Basic",
        columns: ["a", "i", "u", "e", "o"],
        rows: [
            { label: "", cells: ["a", "i", "u", "e", "o"] },
            { label: "K", cells: ["ka", "ki", "ku", "ke", "ko"] },
            { label: "S", cells: ["sa", "shi", "su", "se", "so"] },
            { label: "T", cells: ["ta", "chi", "tsu", "te", "to"] },
            { label: "N", cells: ["na", "ni", "nu", "ne", "no"] },
            { label: "H", cells: ["ha", "hi", "fu", "he", "ho"] },
            { label: "M", cells: ["ma", "mi", "mu", "me", "mo"] },
            { label: "Y", cells: ["ya", null, "yu", null, "yo"] },
            { label: "R", cells: ["ra", "ri", "ru", "re", "ro"] },
            { label: "W", cells: ["wa", null, null, null, "wo"] },
            { label: "N", cells: [null, null, "n", null, null] }
        ]
    },
    {
        title: "Voiced",
        columns: ["a", "i", "u", "e", "o"],
        rows: [
            { label: "G", cells: ["ga", "gi", "gu", "ge", "go"] },
            { label: "Z", cells: ["za", "ji", "zu", "ze", "zo"] },
            { label: "D", cells: ["da", "ji", "zu", "de", "do"] },
            { label: "B", cells: ["ba", "bi", "bu", "be", "bo"] },
            { label: "P", cells: ["pa", "pi", "pu", "pe", "po"] }
        ]
    },
    {
        title: "Combinations",
        columns: ["ya", "yu", "yo"],
        rows: [
            { label: "K", cells: ["kya", "kyu", "kyo"] },
            { label: "S", cells: ["sha", "shu", "sho"] },
            { label: "T", cells: ["cha", "chu", "cho"] },
            { label: "N", cells: ["nya", "nyu", "nyo"] },
            { label: "H", cells: ["hya", "hyu", "hyo"] },
            { label: "M", cells: ["mya", "myu", "myo"] },
            { label: "R", cells: ["rya", "ryu", "ryo"] },
            { label: "G", cells: ["gya", "gyu", "gyo"] },
            { label: "J", cells: ["ja", "ju", "jo"] },
            { label: "B", cells: ["bya", "byu", "byo"] },
            { label: "P", cells: ["pya", "pyu", "pyo"] }
        ]
    },
    {
        title: "Punctuation",
        columns: ["space", ",", ".", "?", "!", "-", "dot"],
        rows: [
            { label: "", cells: ["space", ",", ".", "?", "!", "-", "dot"] }
        ]
    }
];

const normalize = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");

const getKeysForChar = (char) => {
    const raw = [
        char?.romaji,
        char?.reading,
        char?.pronunciation,
        char?.sound,
        char?.label,
        char?.name,
        char?.value
    ];

    const normalized = raw
        .map(normalize)
        .filter(Boolean);

    return [...new Set(normalized)];
};

const EMPTY_CHARACTERS = [];

function CharacterList(props) {
    const characters = props.characters || EMPTY_CHARACTERS;
    const activeSection = props.activeSection;

    const visibleSections = activeSection
        ? sections.filter((section) => section.title === activeSection)
        : sections;

    const lookup = useMemo(
        () =>
            characters.reduce((acc, char) => {
                const keys = getKeysForChar(char);
                keys.forEach((key) => {
                    if (!acc[key]) acc[key] = char;
                });
                return acc;
            }, {}),
        [characters]
    );

    const resolveChar = (cellKey) => {
        if (!cellKey) return null;

        if (cellKey === ".") {
            return (
                lookup["."] ||
                lookup["。"] ||
                characters.find((char) => char?.symbol === "。")
            );
        }

        if (cellKey === "-") {
            return (
                lookup["-"] ||
                lookup["ー"] ||
                characters.find((char) => char?.symbol === "ー")
            );
        }

        return lookup[cellKey] || null;
    };

    return (
        <div className={styles.container}>
            {visibleSections.map((section) => (
                <section key={section.title} className={styles.section}>
                    <div className={styles.sectionHeader}>{section.title}</div>

                    <div
                        className={`${styles.grid} ${section.columns.length > 5 ? styles.compactGrid : ""}`}
                        style={{
                            "--character-columns": section.columns.length
                        }}
                    >
                        <div className={styles.corner} />
                        {section.columns.map((column) => (
                            <div key={`${section.title}-${column}`} className={styles.columnHeader}>
                                {column}
                            </div>
                        ))}

                        {section.rows.map((row) => (
                            <React.Fragment key={`${section.title}-${row.label}-${row.cells.join("-")}`}>
                                <div className={styles.rowLabel}>{row.label}</div>

                                {row.cells.map((cellKey, index) => {
                                    const char = resolveChar(cellKey);

                                    if (!cellKey) {
                                        return (
                                            <div
                                                key={`${section.title}-${row.label}-empty-${index}`}
                                                className={styles.emptyCell}
                                            />
                                        );
                                    }

                                    return (
                                        <div
                                            key={`${section.title}-${row.label}-${cellKey}`}
                                            className={styles.cell}
                                        >
                                            {char ? (
                                                (() => {
                                                    const keys = getKeysForChar(char);
                                                    const highlightKey = props.highlightKey || "";
                                                    const isHighlighted =
                                                        Boolean(highlightKey) &&
                                                        keys.some((k) => highlightKey.endsWith(k));

                                                    return (
                                                        <CharacterSymbolComponent
                                                            char={char}
                                                            handleChange={props.handleChange}
                                                            isHighlighted={isHighlighted}
                                                        />
                                                    );
                                                })()
                                            ) : (
                                                <div className={styles.placeholder}>{cellKey}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}

export default React.memo(CharacterList);
